/**
 * Säsongsbaserad CTA-logik
 * Anpassar CTA-texter automatiskt efter årstid baserat på varumärkesmötet:
 * - Vår (mars-maj): Planera och beställ inför sommaren
 * - Sommar (juni-aug): Njut nu, inspireras av möjligheterna
 * - Höst (sep-nov): Beställ nu, leverans till våren
 * - Vinter (dec-feb): Drömfas, planera inför nästa säsong
 */

export type Season = "spring" | "summer" | "autumn" | "winter";

export interface SeasonalCta {
  season: Season;
  seasonLabel: string;
  title: string;
  subtitle: string;
  buttonText: string;
  urgencyText: string;
}

export function getCurrentSeason(): Season {
  const month = new Date().getMonth(); // 0-11
  if (month >= 2 && month <= 4) return "spring";
  if (month >= 5 && month <= 7) return "summer";
  if (month >= 8 && month <= 10) return "autumn";
  return "winter";
}

const seasonalCtaData: Record<Season, SeasonalCta> = {
  spring: {
    season: "spring",
    seasonLabel: "Vår",
    title: "Planera ditt uterum inför sommaren",
    subtitle:
      "Gå igenom solskydd, ventilation och hur du vill använda ditt uterum. Vi hjälper dig att välja en lösning för huset och tomten.",
    buttonText: "Boka ett kostnadsfritt hembesök",
    urgencyText: "Leveranstid och montering planeras i din offert",
  },
  summer: {
    season: "summer",
    seasonLabel: "Sommar",
    title: "Föreställ dig sommaren i ditt eget uterum",
    subtitle:
      "Ta vara på dina idéer för trädgården. Under ett hembesök går vi igenom placering, material och önskad användningstid.",
    buttonText: "Boka ett kostnadsfritt hembesök",
    urgencyText: "Få en lösning och tidsplan för ditt projekt",
  },
  autumn: {
    season: "autumn",
    seasonLabel: "Höst",
    title: "Planera för fler stunder i uterummet",
    subtitle:
      "Hösten ger tillfälle att tänka igenom hur du vill använda ditt uterum. Vi går igenom husets förutsättningar och tar fram en offert med projektspecifik tidsplan.",
    buttonText: "Planera ditt uterum",
    urgencyText: "Tillverkning och montering planeras efter ditt projekt",
  },
  winter: {
    season: "winter",
    seasonLabel: "Vinter",
    title: "Drömmen börjar med ett samtal",
    subtitle:
      "Börja med att välja hur och när du vill använda rummet. Vi hjälper dig att planera material, konstruktion och nästa steg.",
    buttonText: "Börja planera ditt uterum",
    urgencyText: "Be om en tidsplan i din offert",
  },
};

export function getSeasonalCta(): SeasonalCta {
  return seasonalCtaData[getCurrentSeason()];
}

/**
 * Hämta säsongsanpassad CTA för en specifik kontext
 * Returnerar anpassade texter baserat på sidtyp
 */
export function getSeasonalCtaForContext(
  context: "hero" | "price" | "process" | "general"
): SeasonalCta {
  const base = getSeasonalCta();
  const season = getCurrentSeason();

  // Kontextspecifika anpassningar
  switch (context) {
    case "hero":
      return base; // Använd standardtexterna för hero
    case "price":
      return {
        ...base,
        title: "Få en offert för ditt uterum",
        subtitle:
          "Vi går igenom mått, material, grund och montering så att du kan bedöma hela kostnaden. Offerten anger vad som ingår och när arbetet kan utföras.",
      };
    case "process":
      return {
        ...base,
        title:
          season === "winter"
            ? "Perfekt tid att planera i lugn och ro"
            : season === "summer"
              ? "Boka hembesök och se möjligheterna"
              : base.title,
      };
    case "general":
    default:
      return base;
  }
}
